import { type Color } from '@air/api/types';
import { type AssetFileKind } from '@air/types';

import { type CustomFieldValueFilterBase } from '~/components/Filters/CustomFieldFilter/utils';
import { type ItemTypeFilter } from '~/components/Filters/types';
import { type CustomFieldFilterType, type FilterType } from '~/components/FiltersMenu/types';
import { type SmartTagBase } from '~/components/SmartTagSelect/utils';
import { type TagBase } from '~/components/Tags/utils';
import { type FilterLookupsOptions } from '~/swr-hooks/filters/types';
import { type PersonWithDefaultFace } from '~/swr-hooks/person/types';

export type UploaderFilterType = FilterLookupsOptions['uploaders'][0];

export type FilterLogicType = 'and' | 'or' | 'not';

export const ALL_DATE_RANGE_TYPES = [
  'all-time',
  'today',
  'yesterday',
  'last-week',
  'last-month',
  'last-year',
  'custom-range',
] as const;

export type DateRangeType = (typeof ALL_DATE_RANGE_TYPES)[number];

export const isValidDateRangeType = (value: string | null | undefined): value is DateRangeType => {
  if (!value) return false;
  return ALL_DATE_RANGE_TYPES.some((type) => type === value);
};

export type SelectedFilter = {
  type: FilterType;
  customField?: CustomFieldFilterType;
};

export type DateFilter = {
  type: DateRangeType;
  startDate?: string;
  endDate?: string;
};

export type TagsFilterType = {
  tags: TagBase[];
  logic: FilterLogicType;
};

export type ColorFilter = {
  colors: Color['hex'][];
  threshold: number;
};

export type SmartTagsFilterType = {
  smartTags: SmartTagBase[];
  logic: FilterLogicType;
};

export type PeopleFilterType = {
  logic: FilterLogicType;
  people: PersonWithDefaultFace[];
};

export type ImportedKeywordsFilterType = {
  keywords: string[];
  logic: FilterLogicType;
};

export type OtherFiltersType = 'is-favorited' | 'has-open-comments' | 'has-versions' | 'is-on-boards' | 'is-untagged';

export type CustomFieldSelectedFilter = {
  id: CustomFieldFilterType['id'];
  logic: FilterLogicType;
  values: CustomFieldValueFilterBase[];
};

export type CameraFilter = {
  make: string;
  models: string[];
};

export type LocationsFilter = {
  boards: string[];
  libraries: string[];
};

export const initialSelectedCustomField: CustomFieldSelectedFilter = {
  id: '',
  values: [],
  logic: 'and',
};

type StringArrayFilters = {
  orientations: string[];
  countries: string[];
  creators: string[];
  copyrights: string[];
  cities: string[];
  states: string[];
  videoFrameRates: string[];
  extensions: string[];
  videoAspectRatios: string[];
  sources: string[];
  audioCodings: string[];
  audioSampleRates: string[];
};

export type StringArrayFilterType = keyof StringArrayFilters;

export type Filters = {
  types: ItemTypeFilter[];
  assetFileKind: AssetFileKind[];
  colors: ColorFilter;
  tags: TagsFilterType;
  smartTags: SmartTagsFilterType;
  uploaders: UploaderFilterType[];
  others: OtherFiltersType[];
  customFields: CustomFieldSelectedFilter[];
  createdDate: DateFilter;
  modifiedDate: DateFilter;
  uploadedDate: DateFilter;
  takenDate: DateFilter;
  importedKeywords: ImportedKeywordsFilterType;
  camera: CameraFilter;
  people: PeopleFilterType;
  locations: LocationsFilter;
  similarToImage: string;
  similarTo: string;
} & StringArrayFilters;

export type FiltersState = {
  selectedFilters: SelectedFilter[];
} & Filters;

export const initialFiltersState: FiltersState = {
  selectedFilters: [],
  types: [],
  assetFileKind: [],
  colors: { colors: [], threshold: 0.5 },
  tags: { logic: 'and', tags: [] },
  smartTags: { logic: 'and', smartTags: [] },
  sources: [],
  uploaders: [],
  extensions: [],
  others: [],
  customFields: [],
  createdDate: { type: 'all-time' },
  modifiedDate: { type: 'all-time' },
  uploadedDate: { type: 'all-time' },
  takenDate: { type: 'all-time' },
  importedKeywords: { logic: 'and', keywords: [] },
  orientations: [],
  countries: [],
  copyrights: [],
  creators: [],
  camera: { make: '', models: [] },
  cities: [],
  states: [],
  videoFrameRates: [],
  videoAspectRatios: [],
  audioCodings: [],
  audioSampleRates: [],
  people: { logic: 'and', people: [] },
  locations: { boards: [], libraries: [] },
  similarToImage: '',
  similarTo: '',
};

export type SelectedFilterValueObject = {
  key: keyof Filters;
  value: Filters[keyof Filters];
};
